import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type {
  Aoi,
  AoiWorldPoint,
  SaveAoiInput,
} from "@/features/deployment-dashboard/live-view/model/aoi";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const AOIS_PATH = "/v1/aois";

const areaAoisResponseSchema = z.object({
  area_id: z.string(),
  aois: z.array(
    z.object({
      aoi_id: z.string(),
      aoi_type: z.enum(["customer", "security_lane"]).default("customer"),
      name: z.string(),
      description: z.string().optional(),
      geometry: z.object({
        type: z.literal("Polygon"),
        coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
      }),
      border_style: z.enum(["solid", "dashed", "dotted"]).default("solid"),
      border_color: z.string().default("#2196f3"),
    })
  ),
});

type CreateAoiVariables = SaveAoiInput;

interface UpdateAoiVariables extends SaveAoiInput {
  aoiId: string;
}

function buildAreaAoisPath(areaId: string): string {
  return `/v1/areas/${encodeURIComponent(areaId)}/aois`;
}

function withoutClosingPoint(points: [number, number][]): [number, number][] {
  if (points.length < 2) return points;
  const first = points[0];
  const last = points.at(-1);
  return last && last[0] === first[0] && last[1] === first[1] ? points.slice(0, -1) : points;
}

function toAoiPolygon(points: AoiWorldPoint[]) {
  const first = points[0];
  return {
    type: "Polygon" as const,
    coordinates: [
      [...points.map((point) => [point.x, point.y] as [number, number]), [first.x, first.y]],
    ],
  };
}

function toMutationBody(input: CreateAoiVariables | UpdateAoiVariables) {
  return {
    area_id: input.areaId,
    name: input.name,
    ...(input.description === undefined ? {} : { description: input.description }),
    border_color: input.borderColor,
    geometry: toAoiPolygon(input.points),
  };
}

/** Loads and mutates AOIs for every deployment area included by the live-view filter. */
export function useAreaAois(deploymentId: string, areaIds: string[]) {
  const runtime = useRuntimeHttpClient();
  const queryClient = useQueryClient();
  const client = runtime.client;
  const sessionMatchesDeployment = runtime.deploymentId === deploymentId;
  const canList = runtime.operationGrants.some((grant) => grant.operation_id === "aois_list");
  const canCreate = runtime.operationGrants.some((grant) => grant.operation_id === "aoi_create");
  const canUpdate = runtime.operationGrants.some((grant) => grant.operation_id === "aoi_update");
  const canDelete = runtime.operationGrants.some((grant) => grant.operation_id === "aoi_delete");
  const queryPrefix = ["area-aois", deploymentId] as const;

  const queries = useQueries({
    queries: areaIds.map((areaId) => ({
      queryKey: [...queryPrefix, areaId],
      queryFn: async (): Promise<Aoi[]> => {
        const response = areaAoisResponseSchema.parse(
          await client!.get<unknown>(buildAreaAoisPath(areaId))
        );

        return response.aois.map((aoi) => ({
          id: aoi.aoi_id,
          areaId: response.area_id,
          aoiType: aoi.aoi_type,
          name: aoi.name,
          ...(aoi.description === undefined ? {} : { description: aoi.description }),
          points: withoutClosingPoint(aoi.geometry.coordinates[0] ?? []).map(([x, y]) => ({
            x,
            y,
          })),
          borderStyle: aoi.border_style,
          borderColor: aoi.border_color,
        }));
      },
      enabled: Boolean(client && sessionMatchesDeployment && canList),
    })),
  });
  const aois = queries.flatMap((query) => query.data ?? []);
  const error = queries.find((query) => query.error != null)?.error ?? null;

  const createMutation = useMutation({
    mutationFn: (input: CreateAoiVariables) =>
      client!.post<unknown>(AOIS_PATH, toMutationBody(input)),
    onSuccess: (_response, input) =>
      queryClient.invalidateQueries({ queryKey: ["area-aois", deploymentId, input.areaId] }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateAoiVariables) =>
      client!.put<unknown>(
        `${AOIS_PATH}/${encodeURIComponent(input.aoiId)}`,
        toMutationBody(input)
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryPrefix }),
  });

  const deleteMutation = useMutation({
    mutationFn: (aoiId: string) =>
      client!.delete<void>(`${AOIS_PATH}/${encodeURIComponent(aoiId)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryPrefix }),
  });

  return {
    aois,
    error,
    isPending: queries.some((query) => query.isPending),
    isSuccess: queries.every((query) => query.isSuccess),
    canCreate: Boolean(client && sessionMatchesDeployment && canCreate),
    canUpdate: Boolean(client && sessionMatchesDeployment && canUpdate),
    canDelete: Boolean(client && sessionMatchesDeployment && canDelete),
    createAoi: createMutation.mutateAsync,
    updateAoi: updateMutation.mutateAsync,
    deleteAoi: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    mutationError: createMutation.error ?? updateMutation.error ?? deleteMutation.error,
    resetMutationError: () => {
      createMutation.reset();
      updateMutation.reset();
      deleteMutation.reset();
    },
  };
}
